import { computed } from 'vue';
import { useProductType } from '~/composables/useProductType';
import { useProduct } from '~/modules/product/composables/useProduct';
import safeType from '~/utils/safeType';
import { useBrand } from './useBrand';
import type { MemberProductMapProduct } from '~~/shared/globalTypes/product';
import { getMemberProductMap, getMemberProductIds, getMemberDisplayGroupsMap, getMemberProducts } from '~/utils/product';

function hasMembers(product: Product): product is Product {
  if (!product) {
    return false;
  }

  const { isConsolidatedMaster, isConsolidatedLatchKey, isMaster, isConsolidatedStandard } = useProductType();

  return isConsolidatedMaster || isConsolidatedLatchKey || isMaster || isConsolidatedStandard;
}

export default function useMembersOfProduct() {
  const { productData: product } = useProduct();
  const { isMcom } = useBrand();
  const { isMaster, isShopTheSet } = useProductType();

  if (hasMembers(product)) {
    const memberProductsMap = computed(() => getMemberProductMap(product));
    const allMemberProductIds = computed(() => {
      if (getMemberProductIds(product).length) {
        return getMemberProductIds(product);
      }
      const moreItems = getMemberDisplayGroupsMap(product)['More Items'];
      if (isMcom && moreItems?.length) {
        return safeType(moreItems, []);
      }
      return Object.keys(getMemberProductMap(product));
    });
    const isProductAvailable = (p: MemberProductMapProduct) => {
      if (!p) return false;
      const hasAvailability = p.availability?.available;
      const hasUpcs = p.relationships?.upcs && Object.keys(p.relationships.upcs).length > 0;
      return !!(hasAvailability && hasUpcs);
    };
    const availableMemberProductIds = computed(() => {
      const map = memberProductsMap.value;
      if (!map || Object.keys(map).length === 0) return [];
      return allMemberProductIds.value.filter((id) => isProductAvailable(map[id]));
    });
    const memberProducts = computed(() => getMemberProducts(product));

    const memberProductIds = computed(() => (isMaster || isShopTheSet ? availableMemberProductIds.value : allMemberProductIds.value));
    const memberProductIdsString = computed(() => memberProductIds.value.join(',') || '');

    return {
      memberProductsMap,
      memberProducts,
      memberProductIds,
      memberProductIdsString,
    };
  }
  return {
    memberProductsMap: computed<Record<string, MemberProductMapProduct>>(() => ({})),
    memberProducts: computed(() => []),
    memberProductIds: computed(() => []),
    memberProductIdsString: computed(() => product?.id),
  };
}
